import React from 'react';
import { withStyles } from '@material-ui/core';
import styles from './DashboardDetails.styles';
import FormView from 'components/FormView';
import SummaryTable from 'components/common/SummaryTable';
import DashboardDetailsForm from './DashboardDetails.form';
import IDetailsProperties, { IDashboardDetailsHandle } from './DashboardDetails.properties';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import SummaryTableSchema from './DashboardDetails.summaryTable.schema';
import { AddCircle as AddIcon, RemoveCircle, Edit, Cancel as CancelIcon, CheckCircle } from '@markinson/uicomponents-v2/SvgIcons/';
import { useFetchDashboardDetails, useCreateDashboardDetails, useSetDMEDashboardDetailsQueryData, useUpdateDashboardDetails, useDeleteDashboardDetails } from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';
import { Operation } from 'utils/operations';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';

const DashboardDetails = (props: IDetailsProperties, ref: React.Ref<IDashboardDetailsHandle>) => {
    const { classes, dirty, selectedDashboardId, operationMode, changeOperationMode, formValues, changeConfirmationDialog, resetForm, searchById, search, formSyncErrors, isValid, touchFormFields, formAsyncErrors, searchLoading, currentUser } = props;

    const { data: dashboardDetails, isLoading: fetchDashboardDetailsLoading } = useFetchDashboardDetails(selectedDashboardId);
    const { mutateAsync: createDashboardDetailsMutateAsync, isLoading: createDashboardDetailsLoading } = useCreateDashboardDetails();
    const { mutateAsync: updateDashboardDetailsMutateAsync } = useUpdateDashboardDetails();
    const { mutateAsync: deleteDashboardDetailsMutateAsync } = useDeleteDashboardDetails();
    const setDashboardDetailsQueryData = useSetDMEDashboardDetailsQueryData();
    const { setActionBar } = React.useContext(ActionBarContext);

    const [internalDirty, setInternalDirty] = React.useState<boolean>(false);
    const templateNameFieldRef = React.useRef<IBasicLookupFieldHandle>();
    const isDirty = internalDirty || dirty;
    const params = React.useMemo(() => (new URLSearchParams(location.search)), [location.search]);
    const newDashboardDetailsObject = React.useMemo(
        () => ({
            ActiveDisplay: 'No',
            Active: false,
            DashboardIdLabel: '',
            DashboardId: '0',
            DashboardTypeLabel: '',
            DashboardType: '',
            Description: '',
            LayoutTypeLabel: '',
            LayoutType: '',
            UserIdDescription: '',
            UserIdLabel: currentUser?.name,
            UserId: currentUser?.id,
        }),
        [currentUser]
    );

    React.useEffect(
        () => {
            if (operationMode === Operation.NEW) {
                setTimeout(
                    () => {
                        templateNameFieldRef?.current?.focus();
                    },
                    0
                );
            }
            setActionBarButtons();
        },
        [operationMode, params, dashboardDetails?.DashboardDetail]
    );

    const setActionBarButtons = (): void => {
        setActionBar({
            leftIcons: [],
            centerIcons: [],
            rightIcons: [
                {
                    label: 'Add',
                    Icon: AddIcon,
                    action: 'DashboardDetailsOnAdd',
                    disabled: operationMode === Operation.EDIT || operationMode === Operation.NEW,
                },
                {
                    label: 'Edit',
                    Icon: Edit,
                    disabled: operationMode === Operation.NEW || operationMode === Operation.EDIT || !params.get('DashboardId') || dashboardDetails?.DashboardDetail?.inlineObject?.System,
                    action: 'DashboardDetailsOnEdit',
                },
                {
                    label: 'Delete',
                    Icon: RemoveCircle,
                    disabled: operationMode === Operation.NEW || operationMode === Operation.EDIT || !params.get('DashboardId') || dashboardDetails?.DashboardDetail?.inlineObject?.System,
                    action: 'DashboardDetailsOnDelete',
                },
                {
                    label: 'Cancel',
                    Icon: CancelIcon,
                    disabled: !(operationMode === Operation.EDIT || operationMode === Operation.NEW),
                    action: 'DashboardDetailsCancel',
                },
                {
                    label: 'OK',
                    Icon: CheckCircle,
                    disabled: !(operationMode === Operation.EDIT || operationMode === Operation.NEW),
                    action: 'DashboardDetailsOk',
                },
            ]
        });
    };

    React.useImperativeHandle(
        ref,
        () => ({
            isChangesSavedOrDiscarded(): boolean {
                if (isDirty) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return !isDirty;
            },
            async DashboardDetailsOnAdd(): Promise<void> {
                setInternalDirty(true);
                changeOperationMode(Operation.NEW);
            },
            async DashboardDetailsOnEdit(): Promise<void> {
                setInternalDirty(true);
                changeOperationMode(Operation.EDIT);
            },
            async DashboardDetailsOnDelete(): Promise<void> {
                deleteDashboardDetails(selectedDashboardId);
            },
            async DashboardDetailsCancel(): Promise<void> {
                if (isDirty) {
                    changeConfirmationDialog({
                        open: true,
                        title: 'Discard changes',
                        message: 'Are you sure you want to continue?',
                        okLabel: 'Discard',
                        onCancel: () => undefined,
                        onOk: async () => {
                            resetForm();
                            setInternalDirty(false);
                            changeOperationMode(Operation.BROWSE);
                        }
                    });
                } else {
                    changeOperationMode(Operation.BROWSE);
                }
            },
            async DashboardDetailsOk(): Promise<void> {
                saveDashboardDetails(formValues);
            },
        }),
        [selectedDashboardId, operationMode, formValues, isDirty, formSyncErrors, isValid, formAsyncErrors]
    );

    async function saveDashboardDetailsData(selectedDashId: number, query: any): Promise<any> {

        if (operationMode === Operation.NEW) {

            return createDashboardDetailsMutateAsync({
                ...query,
            });
        } else {

            return updateDashboardDetailsMutateAsync({
                DashboardId: selectedDashId,
                ...query,
            });
        }
    }

    async function saveDashboardDetails(values: any): Promise<void> {
        if (!isValid || !isValidForm(values) || (formSyncErrors && Object.keys(formSyncErrors).length > 0) || Object.keys(formAsyncErrors).length > 0) {
            const requiredError = Object.entries(formSyncErrors).find((item) => item.includes('Required'));
            if (requiredError) {
                showSnackBar({ variant: 'warning', message: 'Please fill in required fields.' });
                Object.entries(formSyncErrors)
                    .filter(([, value]) => value === 'Required')
                    .forEach(([key]) => touchFormFields(key));
            } else {
                showSnackBar({ variant: 'warning', message: 'Please enter valid values' });
            }
        } else {
            const response = await saveDashboardDetailsData(selectedDashboardId, values);
            if (response?.Status && response?.DashboardDetail && response?.DashboardSummary) {
                const { DashboardDetail: details, DashboardSummary } = response;
                if (details && DashboardSummary) {
                    setDashboardDetailsQueryData(Number(selectedDashboardId), {
                        Status: true,
                        DashboardDetail: operationMode !== Operation.NEW ? details : {},
                        DashboardSummary: operationMode !== Operation.NEW ? DashboardSummary : {}
                    });

                    if (operationMode === Operation.NEW) {
                        searchById(details?.inlineObject?.DashboardId);
                    }
                    changeOperationMode(Operation.BROWSE);
                }
            } else {
                showSnackBar({ variant: 'error', message: 'Details failed to save.' });
                resetForm();
                changeOperationMode(Operation.BROWSE);
            }
            setInternalDirty(false);
        }
    }

    async function deleteDashboardDetails(dashboardId: number): Promise<void> {
        const response = await deleteDashboardDetailsMutateAsync({ DashboardId: dashboardId });
        if (response?.Status && response?.Forms[0]?.MessageType === 'Success') {
            search('');
        }
    }

    const isValidForm = (values) => {
        const { DashboardId, UserId } = values;
        if (Object.keys(values).length > 0 && DashboardId && UserId) {
            return true;
        } else {
            return false;
        }
    };

    return (
        <div className={classes.formContainer}>
            <FormView
                isLoading={searchLoading || (fetchDashboardDetailsLoading && params.get('DashboardId'))}
                formName={'DashboardDetailsForm'}
                schema={DashboardDetailsForm}
                includeTabsHeight={false}
                initialValues={operationMode === Operation.NEW ? (newDashboardDetailsObject || {}) : (dashboardDetails?.DashboardDetail?.inlineObject || {})}
                valuesSchema={operationMode === Operation.NEW ? ({}) : (dashboardDetails?.DashboardDetail?.schema || {})}
                operationMode={operationMode}
                summaryTableRenderer={
                    (() => <SummaryTable
                        selectedDashboardSummary={(operationMode === Operation.NEW || createDashboardDetailsLoading) ? {} : { ...dashboardDetails?.DashboardSummary?.inlineObject, dashboardId: dashboardDetails?.DashboardDetail?.inlineObject?.DashboardId }}
                        loadingSelectedDashboardSummary={fetchDashboardDetailsLoading && params.get('DashboardId')}
                        data={SummaryTableSchema}
                    />)}
                fieldExtensions={{
                    Name: {
                        fieldRef: (Ref) => templateNameFieldRef.current = Ref
                    },
                    Active: {
                        disabled: operationMode === Operation.BROWSE || operationMode === Operation.NEW,
                    }
                }}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(DashboardDetails)));
